import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useOidc } from '@/auth/OidcProvider'
import { api } from '@/lib/api'
import type { SchemaDoc } from '@/features/policies/lib/regoSchema'
import type {
  AuditEventItem,
  ConsumptionBlock,
  DecisionLedger,
  DecisionLedgerFilters,
  GraphData,
  MachineClient,
  ModuleListItem,
  OrgItem,
  OrgMember,
  OrgToken,
  PlanAttestation,
  PlanFinding,
  PlanPolicyConfig,
  PlanPolicyResult,
  PlanReplacementByProvider,
  PlanVersionSummary,
  PolicyArtefactType,
  PolicyCoverageItem,
  PolicyDecision,
  PolicyDecisionFeed,
  PolicyEnablement,
  PolicyItem,
  PolicyResultItem,
  PolicySettings,
  PolicyWithOverride,
  ProviderArtefactDetail,
  ProviderArtefactRow,
  ProviderConsumeConfig,
  ProviderFlowItem,
  ProviderFlowState,
  ProviderVcsStats,
  ProviderVersionComparison,
  RegistryUpstream,
  ResourceTreeResponse,
  ServeMode,
  ServeOverrideAction,
  StateFinding,
  StateLockInfo,
  StateVersionSummary,
  StateWorkspaceSummary,
  TenantAccessRules,
  TenantAnalytics,
  TimelineEntry,
  UserProfile,
  VcsTrustEntry,
  VersionDetail,
  VersionListItem,
  WorkspaceActivity,
  WorkspaceLockFileMeta,
  WorkspaceProvider,
} from '@/lib/apiTypes'

/**
 * Reactive CSS media-query match. SPA (no SSR), so the mount-time value is the
 * real one - no initial-false flash. The window/matchMedia guard keeps jsdom
 * unit tests (which lack matchMedia) from throwing.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false,
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])
  return matches
}

export function useAccessToken(): { token: string | null; loading: boolean } {
  const { getAccessToken } = useOidc()
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    getAccessToken()
      .then(setToken)
      .catch(() => setToken(null))
      .finally(() => setLoading(false))
  }, [getAccessToken])
  return { token, loading }
}

/**
 * Generic data-fetching hook with loading + error state.
 *
 * `loading` is true only on the initial load of a resource (and when `deps`
 * change to a resource not yet loaded). A `refetch()` keeps the existing data
 * and surfaces `refreshing` instead, so background polls don't blank the UI.
 *
 * NOTE: `deps` must be primitives (strings/numbers/booleans/null) - they are
 * serialized with JSON.stringify to detect deps changes.
 */
export function useAsync<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
): { data: T | null; loading: boolean; refreshing: boolean; error: string | null; settled: boolean; refetch: () => Promise<T | null> } {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  // Deps key whose fetch last settled (success OR error). State, not a ref: callers
  // gate rendering on it, so the flip must itself schedule a re-render.
  const [settledFor, setSettledFor] = useState<string | null>(null)

  const depsKey = JSON.stringify(deps)
  const settledKey = useRef<string | null>(null) // deps key we have already loaded at least once

  // Resolvers for callers awaiting `refetch()`. Drained when a run settles with the
  // fetched value (null on error - never rejects). A run cancelled by a newer tick /
  // deps change leaves them for that next run, and unmount drains them (null) so an
  // `await refetch()` can never hang on a component that went away.
  const pendingRef = useRef<Array<(value: T | null) => void>>([])
  const drainPending = (value: T | null) => {
    const resolvers = pendingRef.current
    pendingRef.current = []
    for (const resolve of resolvers) resolve(value)
  }
  useEffect(() => () => drainPending(null), [])

  useEffect(() => {
    let cancelled = false
    // Fresh load = first ever, or deps changed to a key we haven't settled yet.
    // StrictMode-safe: we compare against the key we've SETTLED (written only
    // after a successful fetch), so the dev double-invoke of the same deps still
    // counts as a fresh load rather than slipping into the refetch branch.
    const isFreshLoad = settledKey.current !== depsKey

    if (isFreshLoad) {
      setData(null)
      setLoading(true)
      setError(null)
    } else {
      setRefreshing(true) // background refetch - keep data + loading=false
    }

    let result: T | null = null
    fetcher()
      .then((d) => { if (!cancelled) { result = d; setData(d); setError(null); settledKey.current = depsKey } })
      .catch((e: Error) => { if (!cancelled) { setError(e.message) } }) // keep last-good data on refetch failure
      .finally(() => { if (!cancelled) { setLoading(false); setRefreshing(false); setSettledFor(depsKey); drainPending(result) } })

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, tick])

  const refetch = useCallback(() => new Promise<T | null>((resolve) => {
    pendingRef.current.push(resolve)
    setTick((t) => t + 1)
  }), [])
  // `settled` = the data/error we hold was produced for the CURRENT deps. Flips
  // false in the same render that deps change (depsKey advances immediately),
  // so callers never see stale data attributed to the new deps.
  return { data, loading, refreshing, error, settled: settledFor === depsKey, refetch }
}

export const useProfile  = () => useAsync<UserProfile>(() => api.get('/api/auth/me'), [])

export const useModules  = (slug: string, search = '') =>
  useAsync<{ modules: ModuleListItem[]; total: number }>(
    () => api.get(`/api/namespaces/${slug}/modules?search=${encodeURIComponent(search)}&limit=50`),
    [slug, search],
  )

export const useVersions = (slug: string, name: string) =>
  useAsync<{ versions: VersionListItem[] }>(
    () => api.get(`/api/namespaces/${slug}/modules/${name}/versions`),
    [slug, name],
  )

export const useVersionDetail = (slug: string, name: string, provider: string, version: string) =>
  useAsync<VersionDetail>(
    () => api.get(`/v1/modules/${slug}/${name}/${provider}/${version}`),
    [slug, name, provider, version],
  )

export const useAllTokens = () =>
  useAsync<{ tokens: MachineClient[] }>(() => api.get('/api/tokens'), [])

export const useOrgs = () =>
  useAsync<{ orgs: OrgItem[] }>(() => api.get<{ orgs: OrgItem[] }>('/api/orgs'), [])

export const useOrgMembers = (orgId: string) =>
  useAsync<{ members: OrgMember[] }>(() => api.get(`/api/orgs/${orgId}/members`), [orgId])

/** One version's direct dependencies, same node/edge shape as the org graph. */
export const useVersionGraph = (slug: string, name: string, provider: string, version: string) =>
  useAsync<GraphData>(
    () => api.get(`/v1/modules/${slug}/${name}/${provider}/${version}/graph`),
    [slug, name, provider, version],
  )

/** The org's unified graph. Org-scoped, unlike the cross-org endpoint it replaced. */
export const useOrgGraph = (orgSlug: string | null) =>
  useAsync<GraphData>(
    () => orgSlug
      ? api.get(`/api/v1/${orgSlug}/graph`)
      : Promise.resolve({ nodes: [], edges: [] } as GraphData),
    [orgSlug],
  )

/** Type-level policies for an artefact + THIS instance's override state (detail-page section). */
export const useInstancePolicies = (orgId: string, artefactType: PolicyArtefactType, instanceKey: string | null) =>
  useAsync<{ policies: PolicyWithOverride[] }>(
    () => instanceKey
      ? api.get(`/api/orgs/${orgId}/policies/instance?artefact_type=${artefactType}&instance_key=${encodeURIComponent(instanceKey)}`)
      : Promise.resolve({ policies: [] }),
    [orgId, artefactType, instanceKey],
  )

/** Set one instance's override for a policy. */
export const setPolicyOverride = (
  orgId: string, policyId: string,
  body: { artefact_type: PolicyArtefactType; instance_key: string; inherit: boolean; disabled: boolean; enforcing: boolean },
) => api.put(`/api/orgs/${orgId}/policies/${policyId}/override`, body)

/** Force every instance to inherit a value (advisory|enforcing) for a policy. */
export const forcePolicyInheritance = (orgId: string, policyId: string, value: 'advisory' | 'enforcing') =>
  api.post(`/api/orgs/${orgId}/policies/${policyId}/force-inheritance`, { value })

/** The identity "force" action: disable (retained) or re-enable ALL of one identity's custom
 *  policies, so only the inherited org-baseline applies. */
export const forceIdentityCustom = (
  orgId: string,
  body: { artefact_type: PolicyArtefactType; instance_key: string; disabled: boolean },
) => api.post<{ ok: boolean; affected: number }>(`/api/orgs/${orgId}/policies/instance/force`, body)

/** Policies for an org, optionally filtered to a single artefact tab. */
export const useOrgPolicies = (orgId: string, artefactType?: PolicyArtefactType) =>
  useAsync<{ policies: PolicyItem[] }>(
    () => api.get(`/api/orgs/${orgId}/policies${artefactType ? `?artefact_type=${artefactType}` : ''}`),
    [orgId, artefactType],
  )

/**
 * The public JSON Schema for one artefact's OPA `input` document. Unauthenticated,
 * cacheable documentation served straight from the worker's schema module, which is the
 * single source of truth for the contract - it drives the editor's completions and hovers
 * as well as the (?) helper, so neither can drift from what the scanners pass to OPA.
 */
export const usePolicyInputSchema = (artefactType: PolicyArtefactType) =>
  useAsync<SchemaDoc>(() => api.get(`/api/policy-schema/${artefactType}`), [artefactType])

/** Whether policies are effectively enabled for an artefact: global AND toggle AND tier. */
export function isArtefactPolicyEnabled(
  e: PolicyEnablement | null | undefined,
  artefact: 'state' | 'modules' | 'providers',
): boolean {
  if (!e) return false
  return e.opaTier && e.global && e[artefact]
}

export const useOrgPolicySettings = (orgId: string) =>
  useAsync<PolicySettings>(() => api.get(`/api/orgs/${orgId}/policies/settings`), [orgId])

/** The org Danger-zone "block all consumption" kill switch. Admin read, owner write. */
export const useOrgConsumptionBlock = (orgId: string) =>
  useAsync<ConsumptionBlock>(() => api.get(`/api/orgs/${orgId}/policies/consumption-block`), [orgId])

/** Audit events filtered by action (Business+; empty on tiers without audit logging). */
export const useOrgAuditEvents = (orgId: string, action?: string, limit = 20) =>
  useAsync<{ events: AuditEventItem[] }>(
    () => api.get(`/api/orgs/${orgId}/billing/audit?limit=${limit}${action ? `&action=${encodeURIComponent(action)}` : ''}`),
    [orgId, action, limit],
  )

export const useTenantAnalytics = (orgId: string | null) => {
  const result = useAsync<TenantAnalytics>(
    () => orgId ? api.get(`/api/orgs/${orgId}/analytics`) : Promise.resolve(null as unknown as TenantAnalytics),
    [orgId],
  )

  // `fetchedFor` is the slug whose response is currently in `data`/`error`.
  //
  // `selectedOrg` (context) and this hook's state update in separate React
  // commits, so on an org switch there is one commit where `orgId` has already
  // advanced to the new org but `data`/`loading` still describe the old one.
  // Callers gate on `fetchedFor === orgId` to skip rendering that stale frame.
  // `settled` is state inside useAsync, so it flips false the moment `orgId`
  // advances and true (with a re-render) when the fetch for it lands.
  const fetchedFor = result.settled ? orgId : null

  return { ...result, fetchedFor }
}

export const useStateWorkspaces = (orgId: string | null) =>
  useAsync<{ workspaces: StateWorkspaceSummary[] }>(
    () => orgId ? api.get(`/api/v1/${orgId}/state`) : Promise.resolve({ workspaces: [] }),
    [orgId],
  )

/**
 * Every management read/write below takes the workspace KEY in the path, and the key may contain
 * slashes (`stacks/data/platform` - the protocol router matches a wildcard, so terraform is free
 * to use a path-shaped key). The management router matches a single `:workspace` segment, so the
 * key goes in percent-encoded; `openapi/root.yaml`'s `Workspace` parameter is the contract. Pass
 * the raw key in - encoding is this layer's job.
 */
export const useStateVersions = (orgId: string, workspace: string) =>
  useAsync<{ versions: StateVersionSummary[] }>(
    () => api.get(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/versions`),
    [orgId, workspace],
  )

export const useStateActivity = (orgId: string, workspace: string) =>
  useAsync<WorkspaceActivity>(
    () => api.get(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/activity`),
    [orgId, workspace],
  )

/**
 * The merged run timeline for one workspace. Runs that STARTED come from state_apply_runs;
 * refusals come from the ledger. Both are needed: a LOCK refused by policy never creates an
 * apply-run row, which is why the activity rollup alone cannot show a blocked apply.
 */
export const useStateDecisions = (orgId: string, workspace: string, limit = 50) =>
  useAsync<{ entries: TimelineEntry[]; limit: number }>(
    () => api.get(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/decisions?limit=${limit}`),
    [orgId, workspace, limit],
  )

const DECISION_PAGE = 50

function decisionQuery(filters: DecisionLedgerFilters, limit: number, offset: number): string {
  const q = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  if (filters.interaction) q.set('interaction', filters.interaction)
  if (filters.verdict) q.set('verdict', filters.verdict)
  if (filters.policyId) q.set('policy_id', filters.policyId)
  if (filters.q) q.set('q', filters.q)
  if (filters.reqId) q.set('req_id', filters.reqId)
  return q.toString()
}

/**
 * The paginating decision ledger.
 *
 * WHY NOT useAsync WITH A RISING LIMIT. useAsync's fresh-load branch nulls `data` and sets
 * `loading` whenever its deps key changes to one it has not settled, so bumping a limit would blank
 * the table and flash the skeleton on every "Load more" - exactly what the "skeleton on loading,
 * never on refreshing" rule exists to prevent. So page 0 comes from useAsync (keeping the house
 * loading/refreshing/error semantics) and later pages are appended beside it.
 *
 * Appended pages are stored UNDER THEIR FILTER KEY rather than cleared by an effect, following the
 * admin pager's rule that a chain belonging to other filters is ignored, never combined: a stale
 * page can then never render under filters it was not fetched for, even for one frame.
 *
 * `pageSize` is there for the Overview summary, which reads only `facets` and `daily`: both are
 * computed over the whole window regardless of the page, so asking for 50 rows it never renders
 * would be pure transfer. The route clamps the value to [1, 200].
 *
 * This is the first paginating list in the tenant app.
 */
export function useOrgDecisionLedger(
  orgId: string,
  filters: DecisionLedgerFilters,
  pageSize: number = DECISION_PAGE,
): DecisionLedger {
  const key = `${orgId}|${filters.interaction ?? ''}|${filters.verdict ?? ''}|${filters.policyId ?? ''}|${filters.q ?? ''}|${filters.reqId ?? ''}`
  const first = useAsync<PolicyDecisionFeed>(
    () => api.get(`/api/orgs/${orgId}/policies/decisions?${decisionQuery(filters, pageSize, 0)}`),
    [orgId, filters.interaction, filters.verdict, filters.policyId, filters.q, filters.reqId],
  )

  const [more, setMore] = useState<{ key: string; pages: PolicyDecision[][] }>({ key, pages: [] })
  const [loadingMore, setLoadingMore] = useState(false)
  const [moreError, setMoreError] = useState<string | null>(null)

  const appended = more.key === key ? more.pages : []
  const rows = useMemo(
    () => [...(first.data?.decisions ?? []), ...appended.flat()],
    [first.data, appended],
  )

  const total = first.data?.facets.total ?? 0
  const hasMore = rows.length > 0 && rows.length < total

  const loadMore = useCallback(() => {
    if (loadingMore) return
    setLoadingMore(true)
    setMoreError(null)
    api.get<PolicyDecisionFeed>(
      `/api/orgs/${orgId}/policies/decisions?${decisionQuery(filters, pageSize, rows.length)}`,
    )
      .then((page) => {
        setMore((prev) => {
          // The filters moved while this was in flight: drop the page rather than mixing it in.
          const base = prev.key === key ? prev.pages : []
          return { key, pages: [...base, page.decisions] }
        })
      })
      // Keeps the rows already on screen: a failed next page must not blank the table.
      .catch((e: Error) => setMoreError(e.message))
      .finally(() => setLoadingMore(false))
  }, [orgId, key, filters, pageSize, rows.length, loadingMore])

  return {
    rows,
    facets: first.data?.facets ?? null,
    daily: first.data?.daily ?? [],
    windowDays: first.data?.window_days ?? 30,
    loading: first.loading,
    refreshing: first.refreshing,
    error: first.error,
    loadingMore,
    moreError,
    hasMore,
    loadMore,
    refetch: () => { setMore({ key, pages: [] }); void first.refetch() },
  }
}

/**
 * Per-policy coverage. pass/fail are CURRENT counts over the verdict tables, not a time series:
 * a per-tenant pass-rate series cannot exist, because tenant.id is excluded from every worker
 * metric on purpose and the collector strips it. block_count is windowed; the rest is not.
 */
export const useOrgPolicyCoverage = (orgId: string) =>
  useAsync<{ coverage: PolicyCoverageItem[]; window_days: number }>(
    () => api.get(`/api/orgs/${orgId}/policies/coverage`),
    [orgId],
  )

/** Per-version state policy verdicts (breakdown shown in the version detail). */
export const useStatePolicyResults = (orgId: string, workspace: string, versionId: string | null) =>
  useAsync<{ policy_results: PolicyResultItem[] }>(
    () => versionId
      ? api.get(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/versions/${versionId}/policy-results`)
      : Promise.resolve({ policy_results: [] }),
    [orgId, workspace, versionId],
  )

export const useStateFindings = (orgId: string, workspace: string) =>
  useAsync<{ findings: StateFinding[] }>(
    () => api.get(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/findings`),
    [orgId, workspace],
  )

export const useStateLock = (orgId: string, workspace: string) =>
  useAsync<{ lock: StateLockInfo | null }>(
    () => api.get(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/lock`),
    [orgId, workspace],
  )

export const useStateResources = (
  orgId: string,
  workspace: string,
  versionId: string | null,
  compareId?: string | null,
) =>
  useAsync<ResourceTreeResponse | null>(
    () => versionId
      ? api.get(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/versions/${versionId}/resources${compareId ? `?compare=${encodeURIComponent(compareId)}` : ''}`)
      : Promise.resolve(null),
    [orgId, workspace, versionId, compareId ?? ''],
  )

/** Workspaces that can carry plans are the same workspaces as the state backend — a plan
 *  attaches to a state workspace. The org view filters to those with ≥1 plan version. */
export const usePlanWorkspaces = (orgId: string | null) =>
  useAsync<{ workspaces: StateWorkspaceSummary[] }>(
    () => orgId ? api.get(`/api/v1/${orgId}/state`) : Promise.resolve({ workspaces: [] }),
    [orgId],
  )

/** Plan version history for a workspace, newest first. */
export const usePlanVersions = (orgId: string, workspace: string) =>
  useAsync<{ versions: PlanVersionSummary[] }>(
    () => api.get(`/api/v1/${orgId}/plans/${encodeURIComponent(workspace)}/versions`),
    [orgId, workspace],
  )

/** One plan version's summary + its redacted findings (the worker bundles both in one read,
 *  unlike state which splits versions/findings). Powers the per-plan tabbed detail. */
export const usePlanVersion = (orgId: string, workspace: string, versionId: string | null) =>
  useAsync<{ version: PlanVersionSummary; findings: PlanFinding[] } | null>(
    () => versionId
      ? api.get(`/api/v1/${orgId}/plans/${encodeURIComponent(workspace)}/versions/${versionId}`)
      : Promise.resolve(null),
    [orgId, workspace, versionId],
  )

/** Just the redacted findings for a plan version (thin wrapper over the version-detail read). */
export const usePlanFindings = (orgId: string, workspace: string, versionId: string | null) =>
  useAsync<{ findings: PlanFinding[] }>(
    () => versionId
      ? api.get<{ version: PlanVersionSummary; findings: PlanFinding[] }>(`/api/v1/${orgId}/plans/${encodeURIComponent(workspace)}/versions/${versionId}`)
          .then((r) => ({ findings: r.findings }))
      : Promise.resolve({ findings: [] }),
    [orgId, workspace, versionId],
  )

/** Persisted policy-evaluation results for a plan version (M4 gap fix). Powers the Policy tab's
 *  real verdict + reasons, replacing the stopgap that derived the verdict from the `blocked` flag. */
export const usePlanPolicyResults = (orgId: string, workspace: string, versionId: string | null) =>
  useAsync<{ results: PlanPolicyResult[] }>(
    () => versionId
      ? api.get(`/api/v1/${orgId}/plans/${encodeURIComponent(workspace)}/versions/${versionId}/policy`)
      : Promise.resolve({ results: [] }),
    [orgId, workspace, versionId],
  )

/** Apply-matches-review attestation verdict for a known plan version (M5). Drives the Summary tab
 *  attestation status. `null` versionId short-circuits to an unattested verdict. */
export const usePlanAttestation = (orgId: string, workspace: string, versionId: string | null) =>
  useAsync<PlanAttestation>(
    () => versionId
      ? api.get(`/api/v1/${orgId}/plans/${encodeURIComponent(workspace)}/versions/${versionId}/attestation`)
      : Promise.resolve({ attested: false, approved: false, prior_serial: null, current_serial: null, stale: false, reason: 'No plan selected.' }),
    [orgId, workspace, versionId],
  )

/** Cross-workspace forced-replacement correlation for the org (M5): provider → affected workspaces. */
export const usePlanReplacementsByProvider = (orgId: string) =>
  useAsync<PlanReplacementByProvider[]>(
    () => api.get(`/api/v1/${orgId}/plans/replacements-by-provider`),
    [orgId],
  )

/**
 * The effective plan gate config for a scope (admin-only endpoint). `workspace` null reads the
 * org-level default; a workspace name reads that workspace's override (per-workspace wins at
 * evaluation time). Returns `{ config: null }` when the scope has no row.
 */
export const usePlanPolicyConfig = (orgId: string, workspace: string | null, enabled = true) =>
  useAsync<{ config: PlanPolicyConfig | null }>(
    () => !enabled
      ? Promise.resolve({ config: null })
      : api.get(workspace
          ? `/api/v1/${orgId}/plans/${encodeURIComponent(workspace)}/policy-config`
          : `/api/v1/${orgId}/plans/policy-config`),
    [orgId, workspace ?? '', enabled],
  )

/** Upsert a plan gate config scope (org default when workspace null, else per-workspace). */
export const savePlanPolicyConfig = (
  orgId: string,
  workspace: string | null,
  body: { max_destroys: number | null; destroy_denylist: string[]; require_approval: boolean },
) => api.put(
  workspace ? `/api/v1/${orgId}/plans/${encodeURIComponent(workspace)}/policy-config` : `/api/v1/${orgId}/plans/policy-config`,
  body,
)

/** Manually approve a gated plan version (admin + human only). Clears the workspace block ONLY
 *  if this exact plan raised it — `cleared:false` means this plan wasn't the one blocking. */
export const approvePlan = (orgId: string, workspace: string, versionId: string, reason: string) =>
  api.post<{ ok: true; cleared: boolean }>(
    `/api/v1/${orgId}/plans/${encodeURIComponent(workspace)}/versions/${versionId}/approve`,
    { reason },
  )

export const useOrgTokens = (orgId: string) =>
  useAsync<{ tokens: OrgToken[] }>(() => api.get(`/api/orgs/${orgId}/tokens`), [orgId])

export const useVcsTrust = (orgId: string) =>
  useAsync<{ entries: VcsTrustEntry[] }>(() => api.get(`/api/orgs/${orgId}/vcs-trust`), [orgId])

export const useAccessRules = (orgId: string) =>
  useAsync<TenantAccessRules>(() => api.get(`/api/orgs/${orgId}/access-rules`), [orgId])

export const REGISTRY_UPSTREAMS: readonly RegistryUpstream[] = ['terraform.io', 'opentofu.org']

export const useProviderArtefacts = (orgId: string) =>
  useAsync<{ providers: ProviderArtefactRow[] }>(
    () => api.get(`/api/orgs/${orgId}/providers`),
    [orgId],
  )

/** Artefact detail + its versions, fetched on demand when the detail modal opens. A null
 *  namespace/type (modal closed) short-circuits to null instead of firing a bogus request. */
export const useProviderArtefact = (
  orgId: string,
  upstream: string | null,
  namespace: string | null,
  type: string | null,
) =>
  useAsync<ProviderArtefactDetail | null>(
    () => upstream && namespace && type
      ? api.get(`/api/orgs/${orgId}/providers/${encodeURIComponent(upstream)}/${encodeURIComponent(namespace)}/${encodeURIComponent(type)}`)
      : Promise.resolve(null),
    [orgId, upstream, namespace, type],
  )

/** Lazy upstream-vs-patched comparison for a base version. Null args (panel collapsed) short-
 *  circuit to null. Fetched on demand when a security user expands the comparison panel. */
export const useProviderVersionComparison = (
  orgId: string,
  upstream: string | null,
  namespace: string | null,
  type: string | null,
  version: string | null,
) =>
  useAsync<ProviderVersionComparison | null>(
    () => upstream && namespace && type && version
      ? api.get(`/api/orgs/${orgId}/providers/${encodeURIComponent(upstream)}/${encodeURIComponent(namespace)}/${encodeURIComponent(type)}/${encodeURIComponent(version)}/comparison`)
      : Promise.resolve(null),
    [orgId, upstream, namespace, type, version],
  )

/** Consume config for a pinned version, fetched when the consume modal opens. A null
 *  namespace/type/version (modal closed) short-circuits to null instead of firing a request. */
export const useProviderConsume = (
  orgId: string,
  upstream: string | null,
  namespace: string | null,
  type: string | null,
  version: string | null,
) =>
  useAsync<ProviderConsumeConfig | null>(
    () => upstream && namespace && type && version
      ? api.get(
          `/api/orgs/${orgId}/providers/${encodeURIComponent(upstream)}/${encodeURIComponent(namespace)}/${encodeURIComponent(type)}/${encodeURIComponent(version)}/consume`,
        )
      : Promise.resolve(null),
    [orgId, upstream, namespace, type, version],
  )

/** Edit an artefact's version_constraint and/or its single serve_mode (migration 095:
 *  'off' | 'serve' | 'block') — partial body, an omitted field keeps its current value. Mirrors
 *  PATCH /api/orgs/:orgId/providers/:upstream/:namespace/:type. serve_mode='serve' is Business+
 *  (402 below). The registry is part of the identity and is NOT editable (the worker 400s an
 *  `upstream` in the body): add the provider again to serve it from the other registry. */
export const patchProviderArtefact = (
  orgId: string,
  upstream: string,
  namespace: string,
  type: string,
  body: Partial<{
    version_constraint: string
    serve_mode: ServeMode
  }>,
) => api.patch(`/api/orgs/${orgId}/providers/${encodeURIComponent(upstream)}/${encodeURIComponent(namespace)}/${encodeURIComponent(type)}`, body)

/** Download a version's SBOM: mint the short-lived presigned URL, then open it. Called on-demand
 *  (button click) rather than prefetched — the URL is a 300s-TTL presign. */
export async function downloadProviderSbom(
  orgId: string,
  upstream: string,
  namespace: string,
  type: string,
  version: string,
): Promise<void> {
  const { url } = await api.get<{ url: string }>(
    `/api/orgs/${orgId}/providers/${encodeURIComponent(upstream)}/${encodeURIComponent(namespace)}/${encodeURIComponent(type)}/${encodeURIComponent(version)}/sbom`,
  )
  window.open(url, '_blank', 'noopener,noreferrer')
}

/** Set/clear one version's tri-state serve override (admin). null = back to Auto. */
export const setProviderVersionOverride = (
  orgId: string,
  upstream: string,
  namespace: string,
  type: string,
  version: string,
  action: ServeOverrideAction,
  reason?: string,
) =>
  api.put<{ ok: boolean; serve_override: ServeOverrideAction }>(
    `/api/orgs/${orgId}/providers/${encodeURIComponent(upstream)}/${encodeURIComponent(namespace)}/${encodeURIComponent(type)}/versions/${encodeURIComponent(version)}/override`,
    { action, ...(reason ? { reason } : {}) },
  )

/** Targeted, awaitable VCS-stats refresh for one provider (admin). Resolves a missing source
 *  repo from registry metadata, fetches stars/commits/releases and backfills release dates -
 *  or returns the fresh cached row with zero upstream calls. The 15-min cron sweep remains the
 *  steady-state refresher; call this after managing a provider so stats appear immediately. */
export const refreshProviderStats = (orgId: string, upstream: string, namespace: string, type: string) =>
  api.post<{ vcs_stats: ProviderVcsStats | null; reason?: string; release_dates_backfilled: number }>(
    `/api/orgs/${orgId}/providers/${encodeURIComponent(upstream)}/${encodeURIComponent(namespace)}/${encodeURIComponent(type)}/refresh-stats`,
  )

/** Add a provider by hand (admin). POST /api/orgs/:orgId/providers - populates its version
 *  list and enqueues the first scans inline. `upstream` is required: the same (namespace, type)
 *  may be added once per registry. 409 only when the org already has that exact
 *  (upstream, namespace, type) row (auto-discovered or not): edit it with patchProviderArtefact. */
export const manageProvider = (
  orgId: string,
  body: { namespace: string; type: string; upstream: RegistryUpstream; version_constraint?: string },
) =>
  api.post<{ id: string; namespace: string; type: string; discovered: boolean; upstream: RegistryUpstream; version_constraint: string; serve_mode: ServeMode }>(
    `/api/orgs/${orgId}/providers`,
    body,
  )

/** Remove a HAND-ADDED provider the org no longer wants listed (admin). 409 `referenced` when a
 *  lockfile or module surfaced it or still references it - discovery would only recreate it, so
 *  the honest "stop serving" control is the Off / Block serve mode. */
export const removeProvider = (orgId: string, upstream: string, namespace: string, type: string) =>
  api.delete<{ ok: boolean }>(`/api/orgs/${orgId}/providers/${encodeURIComponent(upstream)}/${encodeURIComponent(namespace)}/${encodeURIComponent(type)}`)

export const useWorkspaceProviders = (orgId: string, workspace: string) =>
  /** `truncated` is set when the lock file holds more providers than the worker will return in one
   *  response (its scan-rollup subrequest budget); optional, absent on an older worker. */
  useAsync<{ lock_file: WorkspaceLockFileMeta | null; providers: WorkspaceProvider[]; truncated?: boolean }>(
    () => api.get(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/providers`),
    [orgId, workspace],
  )

export function useCopyToClipboard(): { copy: (text: string) => void; copied: boolean } {
  const [copied, setCopied] = useState(false)
  const mountedRef = useRef(true)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const copy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      if (!mountedRef.current) return
      setCopied(true)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        if (mountedRef.current) setCopied(false)
      }, 2000)
    }).catch(() => {})
  }, [])

  return { copy, copied }
}

/** The org's builds in progress (plus the last 15 minutes of outcomes, so rows do not vanish
 *  mid-watch). Snapshot-backed: no Argo round-trip on the list path. */
export const useProviderFlows = (orgId: string) =>
  useAsync<{ flows: ProviderFlowItem[] }>(
    () => api.get(`/api/orgs/${orgId}/provider-flows`),
    [orgId],
  )

/** One version's flow with the step timeline; fetched on expand (null args short-circuit).
 *  In-flight jobs get a LIVE Argo read server-side; terminal ones answer from D1/snapshot, and
 *  detail=1 pulls the archived workflow once the Workflow Archive is deployed. */
export const useProviderVersionFlow = (
  orgId: string,
  upstream: string | null,
  namespace: string | null,
  type: string | null,
  version: string | null,
  detail = false,
) =>
  useAsync<{ job_id: string | null; flow: ProviderFlowState } | null>(
    () => upstream && namespace && type && version
      ? api.get(`/api/orgs/${orgId}/providers/${encodeURIComponent(upstream)}/${encodeURIComponent(namespace)}/${encodeURIComponent(type)}/versions/${encodeURIComponent(version)}/flow${detail ? '?detail=1' : ''}`)
      : Promise.resolve(null),
    [orgId, upstream, namespace, type, version, detail],
  )
